/* ------------------------------------------------------------------ *
 * FemCare Fitness — workout library (women only).
 * General fitness guidance only; every workout shows the safety note.
 * Each step: { name, secs, cue, rest? }.  `free: true` workouts are
 * open to everyone; the rest unlock with FemCare Premium.
 * ------------------------------------------------------------------ */

export const CATEGORIES = [
  { key: "all", label: "All", color: "#2B2140", bg: "#F3EEF6" },
  { key: "yoga", label: "Yoga", color: "#6B5BEF", bg: "#EFEBFF" },
  { key: "dance", label: "Dance & Zumba-style", color: "#E0306F", bg: "#FFE9EF" },
  { key: "strength", label: "Strength", color: "#B4630E", bg: "#FFF1DC" },
  { key: "cardio", label: "Low-impact cardio", color: "#13896F", bg: "#E3F8F2" },
  { key: "core", label: "Pilates & core", color: "#5A49E0", bg: "#EFEBFF" },
  { key: "stretch", label: "Stretch & mobility", color: "#0E7C9B", bg: "#E2F4FA" },
  { key: "walk", label: "Walking plans", color: "#4E7A12", bg: "#EEF8DF" },
];

export const PHASES = {
  menstrual: { label: "Period (menstrual)", days: "Days 1–5", color: "#FF5C8A", bg: "#FFE9EF",
    tip: "Many women prefer gentle movement now — slow yoga, walking and stretching. Rest is also a good choice." },
  follicular: { label: "Follicular", days: "Days 6–12", color: "#6B5BEF", bg: "#EFEBFF",
    tip: "Energy often rises after your period. A good time to try something new or build strength." },
  ovulation: { label: "Ovulation", days: "Days 13–16", color: "#1FB89A", bg: "#E3F8F2",
    tip: "Many women feel their most energetic — dance cardio and full-body strength can feel great." },
  luteal: { label: "Luteal (pre-period)", days: "Days 17–28", color: "#F29A38", bg: "#FFF1DC",
    tip: "As your period approaches, steady cardio, Pilates and calming yoga may feel better than very intense sessions." },
};

export const SAFETY =
  "General fitness guidance, not medical advice. Warm up, move at your own pace and stop if you feel pain, dizziness or shortness of breath. If you are pregnant, recently gave birth, or have a health condition, check with your doctor before starting a new exercise routine.";

const s = (name, secs, cue) => ({ name, secs, cue });
const rest = (secs = 15) => ({ name: "Rest", secs, cue: "Breathe slowly. Sip water if you need it.", rest: true });

export const WORKOUTS = [
  /* ------------------------------- YOGA ------------------------------- */
  {
    id: "morning-flow", title: "Morning Yoga Flow", category: "yoga", level: "Beginner", minutes: 10, free: true,
    phases: ["follicular", "ovulation", "luteal"], equipment: "Mat (optional)",
    blurb: "Wake up your spine, hips and shoulders with a slow, breath-led flow.",
    steps: [
      s("Easy seat & breathing", 60, "Sit tall, relax your shoulders. Breathe in for 4, out for 6."),
      s("Cat–cow", 60, "On hands and knees, arch and round your back slowly with your breath."),
      s("Thread the needle (right)", 30, "Slide your right arm under your left, rest your shoulder down."),
      s("Thread the needle (left)", 30, "Switch sides. Keep your hips over your knees."),
      s("Downward dog", 45, "Lift your hips up and back. Bend your knees as much as you like."),
      s("Low lunge (right)", 40, "Right foot forward, back knee down. Lift your chest."),
      s("Low lunge (left)", 40, "Switch legs. Sink your hips gently."),
      s("Standing forward fold", 40, "Soft knees, let your head hang heavy."),
      s("Mountain pose with arm reach", 45, "Stand tall, reach both arms up as you breathe in, lower as you breathe out."),
      s("Tree pose (right)", 30, "Foot on calf or thigh — never on the knee. Use a wall for balance."),
      s("Tree pose (left)", 30, "Switch sides."),
      s("Child's pose", 60, "Knees wide, arms forward, rest your forehead down."),
    ],
  },
  {
    id: "period-comfort", title: "Period Comfort Yoga", category: "yoga", level: "Gentle", minutes: 12, free: true,
    phases: ["menstrual", "luteal"], equipment: "Mat, pillow",
    blurb: "Slow, supported poses many women find soothing on heavy or crampy days.",
    steps: [
      s("Belly breathing", 60, "Lie on your back, hands on your belly. Let it rise and fall."),
      s("Knees-to-chest rock", 60, "Hug your knees and rock gently side to side."),
      s("Supine twist (right)", 60, "Drop both knees to the right, look left. Keep shoulders down."),
      s("Supine twist (left)", 60, "Switch sides."),
      s("Bound angle with pillow", 90, "Soles of feet together, knees open, pillows under your knees."),
      s("Child's pose", 90, "Knees wide, rest your chest on a pillow if that feels better."),
      s("Cat–cow (slow)", 60, "Move very slowly, only as far as is comfortable."),
      s("Legs up the wall", 120, "Lie with your legs resting up a wall. Skip if it feels uncomfortable."),
      s("Rest pose", 120, "Lie flat, close your eyes and breathe."),
    ],
  },
  {
    id: "sleep-yoga", title: "Wind-down Yoga for Sleep", category: "yoga", level: "Gentle", minutes: 10, free: false,
    phases: ["menstrual", "follicular", "ovulation", "luteal"], equipment: "Mat or bed",
    blurb: "A calming sequence to release the day before bed.",
    steps: [
      s("Seated side stretch (right)", 40, "Reach your left arm over to the right. Breathe into your ribs."),
      s("Seated side stretch (left)", 40, "Switch sides."),
      s("Seated forward fold", 60, "Fold over straight or bent legs. No forcing."),
      s("Happy baby", 60, "On your back, hold your feet or shins and rock gently."),
      s("Supine twist (right)", 60, "Knees to the right, arms wide."),
      s("Supine twist (left)", 60, "Switch sides."),
      s("Legs up the wall", 120, "Relax your arms by your sides."),
      s("4-7-8 breathing", 60, "In for 4, hold for 7, out for 8. Repeat slowly."),
    ],
  },
  {
    id: "power-yoga", title: "Power Yoga Strength", category: "yoga", level: "Intermediate", minutes: 20, free: false,
    phases: ["follicular", "ovulation"], equipment: "Mat",
    blurb: "A stronger flow that builds heat, balance and full-body strength.",
    steps: [
      s("Sun salutation A × 3", 150, "Reach up, fold, half lift, plank, lower, cobra, down dog. Move with your breath."),
      s("Chair pose", 45, "Sit back as if into a chair, arms up, weight in your heels."),
      s("Warrior II (right)", 45, "Front knee over ankle, arms long, gaze forward."),
      s("Reverse warrior (right)", 30, "Front arm reaches up and back."),
      s("Warrior II (left)", 45, "Switch sides."),
      s("Reverse warrior (left)", 30, "Front arm reaches up and back."),
      rest(20),
      s("Plank hold", 40, "Shoulders over wrists, body long. Drop knees if needed."),
      s("Side plank (right)", 30, "Stack or stagger your feet. Knee down is fine."),
      s("Side plank (left)", 30, "Switch sides."),
      s("Bridge pose", 45, "Feet hip-width, lift your hips, squeeze your glutes."),
      s("Boat pose", 30, "Lean back with a long spine, lift your shins. Hold behind your thighs if needed."),
      s("Pigeon (right)", 60, "Or a figure-four on your back."),
      s("Pigeon (left)", 60, "Switch sides."),
      s("Rest pose", 120, "Lie down and let everything soften."),
    ],
  },
  {
    id: "prenatal-gentle", title: "Gentle Prenatal-friendly Stretch", category: "yoga", level: "Gentle", minutes: 12, free: false,
    phases: [], prenatal: true, equipment: "Mat, chair, pillow",
    blurb: "Supported, low-intensity stretches. Only with your doctor's go-ahead.",
    steps: [
      s("Seated breathing", 60, "Sit on a chair or cushion. Breathe slowly into your sides and back."),
      s("Neck & shoulder rolls", 60, "Slow circles, both directions."),
      s("Seated cat–cow", 60, "Hands on knees, gently arch and round your upper back."),
      s("Side stretch (right)", 40, "Reach over gently, keep both hips grounded."),
      s("Side stretch (left)", 40, "Switch sides."),
      s("Hands-and-knees hip circles", 60, "Slow circles. Stop if anything feels uncomfortable."),
      s("Wide-knee child's pose with pillow", 90, "Knees wide to make room for your bump."),
      s("Side-lying rest", 120, "Lie on your left side with a pillow between your knees."),
    ],
  },

  /* -------------------------- DANCE / ZUMBA-STYLE -------------------------- */
  {
    id: "dance-cardio-beginner", title: "Dance Cardio for Beginners", category: "dance", level: "Beginner", minutes: 15, free: true,
    phases: ["follicular", "ovulation"], equipment: "Your favourite music",
    blurb: "Easy Latin-inspired steps you can follow at home. Put your playlist on and move!",
    steps: [
      s("March in place", 60, "Swing your arms, find the beat."),
      s("Step-touch", 60, "Step right, tap left. Step left, tap right. Add arm swings."),
      s("Grapevine", 60, "Step side, cross behind, step side, tap. Then go the other way."),
      s("Salsa basic", 60, "Forward step, back to centre, back step, back to centre. Let your hips move."),
      rest(20),
      s("Cha-cha side steps", 60, "Side step, side step, quick 1-2-3."),
      s("Merengue march with arms", 60, "Small marching steps with hip sway, arms pumping."),
      s("Cumbia step", 60, "Step back with one foot, return, alternate. Twist your upper body."),
      s("Knee lifts with claps", 60, "Lift each knee and clap under it."),
      rest(20),
      s("Freestyle to your song", 120, "Mix any moves you liked. Just keep moving!"),
      s("Cool-down sway & stretch", 120, "Slow side-to-side sway, then stretch your arms and calves."),
    ],
  },
  {
    id: "bollywood-burn", title: "Bollywood Dance Burn", category: "dance", level: "All levels", minutes: 20, free: false,
    phases: ["follicular", "ovulation"], equipment: "Bollywood playlist",
    blurb: "High-energy Bollywood moves — screw-the-bulb, thumka and bhangra-style bounces.",
    steps: [
      s("Warm-up shoulder shimmies", 60, "Small shoulder shakes while marching."),
      s("Screw-the-bulb", 60, "Twist both hands up high like changing a bulb, step side to side."),
      s("Thumka hip pops", 60, "Pop your hip to the side on the beat, hands on your waist."),
      s("Bhangra bounce", 60, "Bounce on alternate feet with your arms up and shoulders lifting."),
      rest(20),
      s("Garba clap-step", 60, "Step, step, turn slightly and clap. Repeat in a circle."),
      s("Jhatka arm swings", 60, "Quick arm swings across your body with a step."),
      s("Bollywood combo", 120, "Link screw-the-bulb, thumka and bhangra bounce, 8 counts each."),
      rest(20),
      s("Freestyle to the chorus", 120, "Your song, your moves!"),
      s("Cool-down", 120, "Slow walk on the spot, then stretch your legs and arms."),
    ],
  },
  {
    id: "latin-dance-hiit", title: "Latin Dance Party (Zumba-style)", category: "dance", level: "Intermediate", minutes: 25, free: false,
    phases: ["ovulation"], equipment: "Latin playlist",
    blurb: "Longer, faster dance intervals inspired by salsa, merengue and reggaeton.",
    steps: [
      s("Warm-up march & step-touch", 120, "Start easy, find the rhythm."),
      s("Salsa with arms", 90, "Basic salsa with arms circling."),
      s("Merengue turns", 90, "March with hip sway, add a slow turn every 8 counts."),
      rest(20),
      s("Reggaeton bounce", 90, "Bounce with a slight squat, push arms forward."),
      s("Cumbia with arm pumps", 90, "Step back and forward, pump arms on each step."),
      s("Samba side steps", 90, "Quick light side steps, stay on the balls of your feet."),
      rest(30),
      s("Combo round", 180, "Repeat your three favourite moves for 8 counts each."),
      s("Cool-down & stretch", 180, "Slow sway, then quad, calf and chest stretches."),
    ],
  },

  /* ------------------------------ STRENGTH ------------------------------ */
  {
    id: "full-body-home", title: "Full-body Strength at Home", category: "strength", level: "Beginner", minutes: 20, free: true,
    phases: ["follicular", "ovulation"], equipment: "None",
    blurb: "No equipment, no jumping. Builds strength in your legs, glutes, back and arms.",
    steps: [
      s("Warm-up: march & arm circles", 90, "Get warm before you work."),
      s("Squats", 45, "Feet hip-width, sit back, chest up. Use a chair behind you if you like."),
      rest(15),
      s("Wall or knee push-ups", 40, "Hands shoulder-width, lower slowly, push back up."),
      rest(15),
      s("Glute bridges", 45, "On your back, lift your hips and squeeze. Lower slowly."),
      rest(15),
      s("Reverse lunges (alternate)", 45, "Step back and lower your back knee towards the floor."),
      rest(15),
      s("Bird-dog", 45, "On hands and knees, reach opposite arm and leg. Slow and steady."),
      rest(15),
      s("Plank (knees down OK)", 30, "Keep a straight line from head to knees or heels."),
      rest(20),
      s("Round 2: squats", 45, "Same as before."),
      s("Round 2: glute bridges", 45, "Hold the top for a second."),
      s("Cool-down stretch", 120, "Hamstrings, quads, chest and shoulders."),
    ],
  },
  {
    id: "glutes-core", title: "Glutes & Core", category: "strength", level: "Intermediate", minutes: 15, free: false,
    phases: ["follicular", "ovulation"], equipment: "Mat",
    blurb: "Focused lower-body and core work for strong hips and a stable back.",
    steps: [
      s("Hip circles warm-up", 60, "Hands on hips, big slow circles."),
      s("Sumo squats", 45, "Wide stance, toes out, sit down between your heels."),
      s("Single-leg glute bridge (right)", 40, "Other knee hugged in or foot lifted."),
      s("Single-leg glute bridge (left)", 40, "Switch sides."),
      rest(15),
      s("Side-lying leg lifts (right)", 40, "Top leg lifts slowly, toes forward."),
      s("Side-lying leg lifts (left)", 40, "Switch sides."),
      s("Dead bug", 45, "Back flat, lower opposite arm and leg slowly."),
      s("Clamshells (each side 20s)", 40, "Knees bent, open the top knee like a clamshell."),
      rest(15),
      s("Forearm plank", 30, "Knees down is fine."),
      s("Stretch: figure-four", 90, "Ankle over opposite knee, both sides."),
    ],
  },
  {
    id: "upper-body-bottles", title: "Upper Body with Water Bottles", category: "strength", level: "Beginner", minutes: 15, free: false,
    phases: ["follicular", "ovulation", "luteal"], equipment: "2 water bottles or light dumbbells",
    blurb: "Tone your arms, shoulders and upper back with things you already have.",
    steps: [
      s("Arm circles", 60, "Small to big, both directions."),
      s("Bicep curls", 45, "Elbows close to your sides."),
      s("Shoulder press", 45, "Press up overhead, lower to ear level."),
      rest(15),
      s("Bent-over rows", 45, "Hinge forward with a flat back, pull bottles to your ribs."),
      s("Lateral raises", 40, "Lift arms out to the sides to shoulder height."),
      s("Triceps kickbacks", 40, "Hinge forward, straighten your arms back."),
      rest(15),
      s("Wall push-ups", 40, "Slow down, push up."),
      s("Chest & shoulder stretch", 90, "Clasp hands behind your back, then cross-body stretch."),
    ],
  },

  /* --------------------------- LOW-IMPACT CARDIO --------------------------- */
  {
    id: "low-impact-cardio", title: "Low-impact Cardio (No Jumping)", category: "cardio", level: "Beginner", minutes: 12, free: true,
    phases: ["follicular", "ovulation", "luteal"], equipment: "None",
    blurb: "Raise your heart rate without jumping — kind to knees and neighbours.",
    steps: [
      s("March in place", 60, "Pump your arms."),
      s("Step jacks", 40, "Step one foot out while raising your arms, alternate."),
      s("Knee drives", 40, "Drive alternate knees up to your hands."),
      rest(15),
      s("Squat to reach", 40, "Squat, then stand and reach up high."),
      s("Skater steps", 40, "Step wide to the side, swing your arms across."),
      s("Punches", 40, "Quick punches forward with a slight knee bend."),
      rest(15),
      s("Round 2: step jacks", 40, "Faster if you can."),
      s("Round 2: knee drives", 40, "Keep your core tight."),
      s("Round 2: skater steps", 40, "Bigger steps."),
      s("Cool-down walk & stretch", 120, "Slow march, then calf and quad stretches."),
    ],
  },
  {
    id: "steady-cardio-30", title: "Steady Cardio 30", category: "cardio", level: "All levels", minutes: 30, free: false,
    phases: ["luteal", "follicular"], equipment: "None, or a treadmill/cycle",
    blurb: "A steady session at a 'can talk, can't sing' effort.",
    steps: [
      s("Warm-up at easy pace", 300, "Walk, march or cycle easy."),
      s("Steady effort", 1200, "You can talk in short sentences, but not sing."),
      s("Cool-down at easy pace", 300, "Slow down gradually."),
      s("Stretch", 0, "Calves, quads and hamstrings."),
    ].filter((x) => x.secs > 0),
  },

  /* ---------------------------- PILATES / CORE ---------------------------- */
  {
    id: "pelvic-floor-core", title: "Core & Pelvic Floor Basics", category: "core", level: "Gentle", minutes: 10, free: true,
    phases: ["menstrual", "follicular", "ovulation", "luteal"], equipment: "Mat",
    blurb: "Learn to connect your breath, deep core and pelvic floor.",
    steps: [
      s("Connection breath", 60, "Breathe in and let your belly and pelvic floor relax; breathe out and gently lift, like stopping wind."),
      s("Heel slides", 60, "On your back, slide one heel out and back, keeping your pelvis still."),
      s("Toe taps", 60, "Knees lifted at 90°, tap one toe down at a time."),
      s("Pelvic tilts", 60, "Flatten and gently arch your lower back."),
      s("Glute bridge with breath", 60, "Breathe out as you lift."),
      s("Side-lying clamshells", 60, "30 seconds each side."),
      s("Bird-dog", 60, "Slow, controlled, opposite arm and leg."),
      s("Child's pose", 60, "Breathe into your back."),
    ],
  },
  {
    id: "pilates-mat", title: "Pilates Mat Flow", category: "core", level: "Intermediate", minutes: 18, free: false,
    phases: ["follicular", "luteal"], equipment: "Mat",
    blurb: "Classic Pilates-style moves for core strength, posture and control.",
    steps: [
      s("The hundred (modified)", 60, "Head down or up, pump your arms, breathe in for 5, out for 5."),
      s("Roll-ups (or half roll-backs)", 60, "Peel up and down one vertebra at a time."),
      s("Single-leg stretch", 60, "Switch legs with control."),
      s("Criss-cross", 45, "Slow rotation, elbows wide."),
      rest(15),
      s("Swimming", 45, "On your belly, flutter opposite arm and leg."),
      s("Side kicks (right)", 45, "Kick forward and back, hips stacked."),
      s("Side kicks (left)", 45, "Switch sides."),
      s("Saw", 60, "Seated, twist and reach to the opposite foot."),
      s("Spine stretch forward", 60, "Sit tall, round forward with a breath out."),
      s("Rest pose", 60, "Relax and breathe."),
    ],
  },

  /* --------------------------- STRETCH & MOBILITY --------------------------- */
  {
    id: "desk-stretch", title: "5-minute Desk Stretch", category: "stretch", level: "Gentle", minutes: 5, free: true,
    phases: ["menstrual", "follicular", "ovulation", "luteal"], equipment: "Chair",
    blurb: "Release your neck, shoulders and hips between meetings.",
    steps: [
      s("Neck side stretch", 40, "Ear towards shoulder, both sides."),
      s("Shoulder rolls", 30, "Backwards and forwards."),
      s("Seated twist", 40, "Both sides, sit tall."),
      s("Chest opener", 30, "Clasp hands behind your back, lift your chest."),
      s("Seated figure-four", 60, "Ankle on opposite knee, lean forward slightly. Both sides."),
      s("Wrist & finger stretch", 30, "Gently pull fingers back, both hands."),
      s("Stand & reach", 30, "Stand up, reach high, then fold forward."),
    ],
  },
  {
    id: "full-body-mobility", title: "Full-body Mobility", category: "stretch", level: "All levels", minutes: 12, free: false,
    phases: ["menstrual", "luteal"], equipment: "Mat",
    blurb: "Loosen tight hips, hamstrings and upper back.",
    steps: [
      s("World's greatest stretch (right)", 60, "Lunge, hand down, rotate and reach up."),
      s("World's greatest stretch (left)", 60, "Switch sides."),
      s("90/90 hip switches", 60, "Seated, rotate your knees side to side."),
      s("Thoracic open book (right)", 45, "Side-lying, open your top arm like a book."),
      s("Thoracic open book (left)", 45, "Switch sides."),
      s("Hamstring stretch with strap", 60, "Use a towel or scarf. Both legs."),
      s("Hip flexor stretch", 60, "Half-kneeling, both sides."),
      s("Cat–cow", 60, "Slow and smooth."),
      s("Child's pose", 60, "Breathe deeply."),
    ],
  },

  /* ------------------------------- WALKING ------------------------------- */
  {
    id: "brisk-walk-30", title: "30-minute Brisk Walk", category: "walk", level: "All levels", minutes: 30, free: true,
    phases: ["menstrual", "follicular", "ovulation", "luteal"], equipment: "Comfortable shoes",
    blurb: "A simple guided walk with pace changes to keep it interesting.",
    steps: [
      s("Easy warm-up walk", 300, "Relaxed pace."),
      s("Brisk walk", 240, "Walk like you're a little late."),
      s("Moderate walk", 120, "Slightly easier."),
      s("Brisk walk", 240, "Pump your arms."),
      s("Moderate walk", 120, "Breathe steadily."),
      s("Brisk walk", 240, "Strong finish."),
      s("Easy cool-down walk", 300, "Slow down gradually."),
      s("Calf & quad stretch", 240, "Both legs."),
    ],
  },
  {
    id: "walk-plan-beginner", title: "Beginner Walking Intervals", category: "walk", level: "Beginner", minutes: 20, free: false,
    phases: ["menstrual", "luteal"], equipment: "Comfortable shoes",
    blurb: "Short brisk intervals with easy recovery — perfect for getting started.",
    steps: [
      s("Easy warm-up walk", 240, "Relaxed pace."),
      s("Brisk", 60, "Pick up the pace."),
      s("Easy", 120, "Recover."),
      s("Brisk", 60, "Pick up the pace."),
      s("Easy", 120, "Recover."),
      s("Brisk", 60, "Pick up the pace."),
      s("Easy", 120, "Recover."),
      s("Brisk", 60, "Last one!"),
      s("Cool-down walk", 240, "Slow and easy."),
      s("Stretch", 120, "Calves and hamstrings."),
    ],
  },
];

export const totalSecs = (w) => w.steps.reduce((t, x) => t + x.secs, 0);

export function phaseForDay(day, cycleLen = 28) {
  const ovu = cycleLen - 14;
  if (day <= 5) return "menstrual";
  if (day < ovu - 1) return "follicular";
  if (day <= ovu + 2) return "ovulation";
  return "luteal";
}
